import React, { useState } from "react";
import { useCookies } from "react-cookie";
import background from "assets/img/background.jpg";
import logo from "assets/img/logo_login.svg";
import { API_URI } from "../helpers";

const colors = {
  bg: "#0F172A",
  card: "#FFFFFF",
  text: "#111827",
  muted: "#6B7280",
  border: "#E5E7EB",
  accent: "#4F46E5",
  accentHover: "#4338CA",
  errorBg: "#FEF2F2",
  errorBorder: "#FECACA",
  errorText: "#B91C1C",
};

const styles = {
  wrapper: {
    minHeight: "100vh",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.bg,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundPosition: "center",
    fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
    padding: 16,
    boxSizing: "border-box",
  },
  card: {
    width: "100%",
    maxWidth: 380,
    background: colors.card,
    borderRadius: 12,
    padding: 32,
    boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
    boxSizing: "border-box",
  },
  logoBox: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 24,
  },
  logo: { height: 44, width: "auto" },
  title: {
    fontSize: 18,
    fontWeight: 600,
    color: colors.text,
    margin: "0 0 4px",
    textAlign: "center",
  },
  subtitle: {
    fontSize: 13,
    color: colors.muted,
    margin: "0 0 24px",
    textAlign: "center",
  },
  field: { marginBottom: 14 },
  label: {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: colors.text,
    marginBottom: 6,
    letterSpacing: 0.2,
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s, box-shadow 0.15s",
    fontFamily: "inherit",
  },
  inputError: {
    borderColor: colors.errorText,
    boxShadow: `0 0 0 3px ${colors.errorBg}`,
  },
  alert: {
    padding: "10px 12px",
    background: colors.errorBg,
    border: `1px solid ${colors.errorBorder}`,
    color: colors.errorText,
    borderRadius: 8,
    fontSize: 13,
    marginBottom: 14,
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
  },
  remember: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: colors.muted,
    marginBottom: 18,
    cursor: "pointer",
    userSelect: "none",
  },
  button: {
    width: "100%",
    padding: "11px 16px",
    background: colors.accent,
    color: "#fff",
    border: 0,
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    transition: "background 0.15s",
  },
  buttonDisabled: { opacity: 0.6, cursor: "not-allowed" },
};

export default function Login(props) {
  const [cookies, setCookie, removeCookie] = useCookies(["api"]);
  const [user, setUser] = useState(cookies.lastuser || "");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState("lastuser" in cookies);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState({ user: false, password: false });

  const userMissing = touched.user && !user.trim();
  const passMissing = touched.password && !password;

  const submit = (e) => {
    if (e) e.preventDefault();
    setTouched({ user: true, password: true });
    setError("");

    if (!user.trim() || !password) {
      setError("Ingresá usuario y contraseña.");
      return;
    }

    setLoading(true);
    const data = new FormData();
    data.append("user", user.trim());
    data.append("password", password);

    fetch(`${API_URI}/admin/login`, { method: "POST", body: data })
      .then((res) => res.json().then((j) => ({ status: res.status, body: j })))
      .then(({ status, body }) => {
        setLoading(false);
        if (body && body.result) {
          const opts = { path: "/", maxAge: 86400 };
          setCookie("profile", body.profile, opts);
          setCookie("api", body.token, opts);
          if (remember) {
            setCookie("lastuser", user.trim(), { path: "/", maxAge: 86400 * 30 });
          } else {
            removeCookie("lastuser");
          }
          if (body.profile === "admin") {
            props.history.push("/admin/dashboard-ad-spend");
          } else {
            props.history.push("/admin/changePassword");
          }
        } else {
          setError(
            (body && body.error) ||
              (status === 401
                ? "Credenciales inválidas."
                : "No se pudo iniciar sesión. Intentá de nuevo.")
          );
        }
      })
      .catch(() => {
        setLoading(false);
        setError("No se pudo conectar al servidor. Verificá tu conexión.");
      });
  };

  const onKey = (e) => {
    if (e.key === "Enter") submit(e);
  };

  return (
    <div
      style={{
        ...styles.wrapper,
        backgroundImage: `linear-gradient(rgba(15,23,42,0.55), rgba(15,23,42,0.85)), url(${background})`,
      }}
    >
      <form style={styles.card} onSubmit={submit} noValidate>
        <div style={styles.logoBox}>
          <img src={logo} alt="TCDG" style={styles.logo} />
        </div>
        <h1 style={styles.title}>Acceso al panel</h1>
        <p style={styles.subtitle}>Ingresá con tu usuario administrador</p>

        {error && (
          <div style={styles.alert}>
            <span>⚠</span>
            <span>{error}</span>
          </div>
        )}

        <div style={styles.field}>
          <label style={styles.label} htmlFor="user">
            Usuario
          </label>
          <input
            id="user"
            type="text"
            autoComplete="username"
            autoFocus
            value={user}
            onChange={(e) => setUser(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, user: true }))}
            onKeyDown={onKey}
            disabled={loading}
            style={{
              ...styles.input,
              ...(userMissing ? styles.inputError : {}),
            }}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label} htmlFor="password">
            Contraseña
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, password: true }))}
            onKeyDown={onKey}
            disabled={loading}
            style={{
              ...styles.input,
              ...(passMissing ? styles.inputError : {}),
            }}
          />
        </div>

        <label style={styles.remember}>
          <input
            type="checkbox"
            checked={remember}
            onChange={() => setRemember(!remember)}
            disabled={loading}
          />
          Recordarme en este equipo
        </label>

        <button
          type="submit"
          disabled={loading}
          style={{
            ...styles.button,
            ...(loading ? styles.buttonDisabled : {}),
          }}
        >
          {loading ? "Ingresando…" : "Ingresar"}
        </button>
      </form>
    </div>
  );
}
